let openCvPromise

function loadOpenCv() {
  if (window.cv && (window.cv.Mat || typeof window.cv.then === 'function')) {
    return typeof window.cv.then === 'function' ? window.cv : Promise.resolve(window.cv)
  }
  if (!openCvPromise) {
    openCvPromise = new Promise((resolve, reject) => {
      const resolveRuntime = () => {
        const runtime = window.cv
        if (runtime && typeof runtime.then === 'function') {
          runtime.then(resolve).catch(reject)
          return
        }
        if (runtime && runtime.Mat) {
          resolve(runtime)
          return
        }
        reject(new Error('OpenCV runtime is unavailable'))
      }

      const existingScript = document.querySelector('script[data-ktp-opencv]')
      if (existingScript) {
        existingScript.addEventListener('load', resolveRuntime, { once: true })
        existingScript.addEventListener('error', () => reject(new Error('Unable to load OpenCV')), { once: true })
        return
      }

      const script = document.createElement('script')
      const baseUrl = (process.env.BASE_URL || '/rental-h5/').replace(/\/?$/, '/')
      script.src = `${baseUrl}vendor/opencv.js`
      script.async = true
      script.setAttribute('data-ktp-opencv', 'true')
      script.onload = resolveRuntime
      script.onerror = () => reject(new Error('Unable to load OpenCV'))
      document.head.appendChild(script)
    })
  }
  return openCvPromise
}

function getImageStats(canvas) {
  const context = canvas.getContext('2d', { willReadFrequently: true })
  const pixels = context.getImageData(0, 0, canvas.width, canvas.height).data
  let brightness = 0
  let brightnessSquared = 0
  let edgeCount = 0

  for (let y = 1; y < canvas.height - 1; y += 1) {
    for (let x = 1; x < canvas.width - 1; x += 1) {
      const index = (y * canvas.width + x) * 4
      const previousX = index - 4
      const previousY = index - canvas.width * 4
      const current = pixels[index] * 0.299 + pixels[index + 1] * 0.587 + pixels[index + 2] * 0.114
      const xGray = pixels[previousX] * 0.299 + pixels[previousX + 1] * 0.587 + pixels[previousX + 2] * 0.114
      const yGray = pixels[previousY] * 0.299 + pixels[previousY + 1] * 0.587 + pixels[previousY + 2] * 0.114
      brightness += current
      brightnessSquared += current * current
      if (Math.abs(current - xGray) + Math.abs(current - yGray) > 58) edgeCount += 1
    }
  }

  const sampleCount = Math.max(1, (canvas.width - 2) * (canvas.height - 2))
  brightness /= sampleCount
  return {
    brightness,
    variance: brightnessSquared / sampleCount - brightness * brightness,
    edgeDensity: edgeCount / sampleCount
  }
}

function getContourPoints(contour) {
  const values = contour.data32S || contour.data32F
  const points = []
  for (let index = 0; index < values.length; index += 2) {
    points.push({ x: values[index], y: values[index + 1] })
  }
  return points
}

function distance(first, second) {
  return Math.hypot(first.x - second.x, first.y - second.y)
}

function clamp(value, min, max) {
  return Math.max(min, Math.min(max, value))
}

function detectDocumentLines(canvas) {
  const width = canvas.width
  const height = canvas.height
  const context = canvas.getContext('2d', { willReadFrequently: true })
  const pixels = context.getImageData(0, 0, width, height).data
  const gray = new Uint8Array(width * height)
  const gradientX = new Float32Array(width * height)
  const gradientY = new Float32Array(width * height)
  let gradientTotal = 0

  for (let index = 0; index < gray.length; index += 1) {
    const pixel = index * 4
    gray[index] = pixels[pixel] * 0.299 + pixels[pixel + 1] * 0.587 + pixels[pixel + 2] * 0.114
  }

  for (let y = 1; y < height - 1; y += 1) {
    for (let x = 1; x < width - 1; x += 1) {
      const index = y * width + x
      const gx = Math.abs(gray[index] - gray[index - 1])
      const gy = Math.abs(gray[index] - gray[index - width])
      gradientX[index] = gx
      gradientY[index] = gy
      gradientTotal += (gx + gy) / 2
    }
  }

  const averageGradient = gradientTotal / Math.max(1, (width - 2) * (height - 2))
  const lineThreshold = Math.max(10, averageGradient * 1.25 + 4)
  const scoreVerticalLine = x => {
    const segmentSize = height * 0.82 / 8
    let total = 0
    let continuousSegments = 0
    for (let segment = 0; segment < 8; segment += 1) {
      const start = Math.round(height * 0.09 + segment * segmentSize)
      const end = Math.min(height - 1, Math.round(start + segmentSize))
      let score = 0
      let count = 0
      for (let y = start; y < end; y += 1) {
        for (let offset = -2; offset <= 2; offset += 1) {
          const sampleX = clamp(x + offset, 1, width - 2)
          score += gradientX[y * width + sampleX]
          count += 1
        }
      }
      const segmentScore = score / Math.max(1, count)
      total += segmentScore
      if (segmentScore >= lineThreshold) continuousSegments += 1
    }
    return {
      average: total / 8,
      continuity: continuousSegments / 8
    }
  }
  const scoreHorizontalLine = y => {
    const segmentSize = width * 0.82 / 8
    let total = 0
    let continuousSegments = 0
    for (let segment = 0; segment < 8; segment += 1) {
      const start = Math.round(width * 0.09 + segment * segmentSize)
      const end = Math.min(width - 1, Math.round(start + segmentSize))
      let score = 0
      let count = 0
      for (let x = start; x < end; x += 1) {
        for (let offset = -2; offset <= 2; offset += 1) {
          const sampleY = clamp(y + offset, 1, height - 2)
          score += gradientY[sampleY * width + x]
          count += 1
        }
      }
      const segmentScore = score / Math.max(1, count)
      total += segmentScore
      if (segmentScore >= lineThreshold) continuousSegments += 1
    }
    return {
      average: total / 8,
      continuity: continuousSegments / 8
    }
  }
  const findBestLine = (start, end, scorer) => {
    let best = { average: 0, continuity: 0, position: 0 }
    for (let position = Math.round(start); position <= Math.round(end); position += 2) {
      const candidate = scorer(position)
      const candidateScore = candidate.average * (0.55 + candidate.continuity)
      const bestScore = best.average * (0.55 + best.continuity)
      if (candidateScore > bestScore) best = { ...candidate, position }
    }
    return best
  }

  const left = findBestLine(width * 0.03, width * 0.40, scoreVerticalLine)
  const right = findBestLine(width * 0.60, width * 0.97, scoreVerticalLine)
  const top = findBestLine(height * 0.03, height * 0.40, scoreHorizontalLine)
  const bottom = findBestLine(height * 0.60, height * 0.97, scoreHorizontalLine)
  const lines = [left, right, top, bottom]
  const lineConfidence = Math.min(...lines.map(line => clamp(line.average / lineThreshold, 0, 1)))
  const continuity = Math.min(...lines.map(line => line.continuity))
  const cardWidth = right.position - left.position
  const cardHeight = bottom.position - top.position
  const cardWidthRatio = cardWidth / width
  const cardHeightRatio = cardHeight / height
  const cardRatio = cardWidth / Math.max(1, cardHeight)
  const areaRatio = cardWidthRatio * cardHeightRatio
  const guideWidth = width * 0.92
  const guideHeight = guideWidth / (85.6 / 53.98)
  const guideLeft = (width - guideWidth) / 2
  const guideTop = (height - guideHeight) / 2
  const guideTolerance = width * 0.04
  const insideGuide = left.position >= guideLeft - guideTolerance &&
    top.position >= guideTop - guideTolerance &&
    right.position <= guideLeft + guideWidth + guideTolerance &&
    bottom.position <= guideTop + guideHeight + guideTolerance
  const sizeValid = cardWidthRatio >= 0.48 && cardHeightRatio >= 0.36 && areaRatio >= 0.22
  const geometryValid = sizeValid && cardRatio >= 1.05 && cardRatio <= 2.25
  const score = lineConfidence * (0.55 + continuity) * areaRatio
  const candidate = { score, ratio: cardRatio, areaRatio, rectangularity: 1, sizeValid, insideGuide }

  return {
    valid: lineConfidence >= 0.50 && continuity >= 0.20 && geometryValid && insideGuide ? candidate : null,
    candidate: geometryValid || lineConfidence >= 0.45 ? candidate : null
  }
}

async function detectDocumentContour(canvas) {
  const cv = await loadOpenCv()
  const source = cv.imread(canvas)
  const gray = new cv.Mat()
  const blurred = new cv.Mat()
  const edges = new cv.Mat()
  const contours = new cv.MatVector()
  const hierarchy = new cv.Mat()
  let best = null
  let bestCandidate = null

  try {
    cv.cvtColor(source, gray, cv.COLOR_RGBA2GRAY)
    cv.GaussianBlur(gray, blurred, new cv.Size(5, 5), 0, 0, cv.BORDER_DEFAULT)
    cv.Canny(blurred, edges, 30, 100)
    cv.findContours(edges, contours, hierarchy, cv.RETR_LIST, cv.CHAIN_APPROX_SIMPLE)

    for (let index = 0; index < contours.size(); index += 1) {
      const contour = contours.get(index)
      const perimeter = cv.arcLength(contour, true)
      const approximation = new cv.Mat()
      cv.approxPolyDP(contour, approximation, Math.max(2, perimeter * 0.035), true)

      if (approximation.rows === 4) {
        const points = getContourPoints(approximation)
        const area = Math.abs(cv.contourArea(approximation))
        const bounds = cv.boundingRect(approximation)
        const sideLengths = points.map((point, pointIndex) => distance(point, points[(pointIndex + 1) % points.length]))
        const averageWidth = (sideLengths[0] + sideLengths[2]) / 2
        const averageHeight = (sideLengths[1] + sideLengths[3]) / 2
        const ratio = Math.max(averageWidth, averageHeight) / Math.max(1, Math.min(averageWidth, averageHeight))
        const rectangularity = area / Math.max(1, bounds.width * bounds.height)
        const areaRatio = area / (canvas.width * canvas.height)
        const guideWidth = canvas.width * 0.92
        const guideHeight = guideWidth / (85.6 / 53.98)
        const guideLeft = (canvas.width - guideWidth) / 2
        const guideTop = (canvas.height - guideHeight) / 2
        const guideRight = guideLeft + guideWidth
        const guideBottom = guideTop + guideHeight
        const guideTolerance = canvas.width * 0.01
        const insideGuide = bounds.x >= guideLeft - guideTolerance &&
          bounds.y >= guideTop - guideTolerance &&
          bounds.x + bounds.width <= guideRight + guideTolerance &&
          bounds.y + bounds.height <= guideBottom + guideTolerance
        const centerX = bounds.x + bounds.width / 2
        const centerY = bounds.y + bounds.height / 2
        const centered = Math.abs(centerX - canvas.width / 2) <= canvas.width * 0.20 &&
          Math.abs(centerY - canvas.height / 2) <= canvas.height * 0.20
        const shapeValid = centered &&
          areaRatio >= 0.16 &&
          bounds.width >= canvas.width * 0.42 &&
          bounds.height >= canvas.height * 0.28 &&
          ratio >= 1.15 &&
          ratio <= 2.15 &&
          rectangularity >= 0.62
        const sizeValid = areaRatio >= 0.42 &&
          bounds.width >= canvas.width * 0.60 &&
          bounds.height >= canvas.height * 0.45

        if (shapeValid) {
          const ratioScore = 1 - Math.min(0.5, Math.abs(ratio - (85.6 / 53.98)) / 3)
          const score = areaRatio * rectangularity * ratioScore
          const candidate = { score, ratio, areaRatio, rectangularity, sizeValid, insideGuide }
          if (!bestCandidate || score > bestCandidate.score) bestCandidate = candidate
          if (sizeValid && insideGuide && (!best || score > best.score)) {
            best = candidate
          }
        }
      }
      approximation.delete()
      contour.delete()
    }
  } finally {
    source.delete()
    gray.delete()
    blurred.delete()
    edges.delete()
    contours.delete()
    hierarchy.delete()
  }

  return { valid: best, candidate: bestCandidate }
}

export async function inspectKtpCanvas(canvas) {
  const stats = getImageStats(canvas)
  let contourResult = { valid: null, candidate: null }
  const lineResult = detectDocumentLines(canvas)
  try {
    contourResult = await detectDocumentContour(canvas)
  } catch (error) {
    // OpenCV 加载失败时保持不通过，避免退化成颜色或亮度误判。
    contourResult = { valid: null, candidate: null }
  }

  const contour = contourResult.valid || lineResult.valid
  const candidate = contourResult.candidate || lineResult.candidate
  const tooSmall = !contour && !!candidate && candidate.insideGuide && !candidate.sizeValid
  const outOfFrame = !contour && !!candidate && !candidate.insideGuide
  const qualityPassed = stats.brightness > 28 &&
    stats.brightness < 240 &&
    stats.variance > 80 &&
    stats.edgeDensity > 0.012
  const cardDetected = !!contour
  return {
    passed: cardDetected && qualityPassed,
    cardDetected,
    brightness: Math.round(stats.brightness),
    edgeDensity: Number(stats.edgeDensity.toFixed(3)),
    contourScore: contour ? Number(contour.score.toFixed(3)) : 0,
    documentAreaRatio: contour ? Number(contour.areaRatio.toFixed(2)) : candidate ? Number(candidate.areaRatio.toFixed(2)) : 0,
    reason: !cardDetected ? outOfFrame ? 'out-of-frame' : tooSmall ? 'too-small' : 'no-card' : stats.brightness <= 28 ? 'too-dark' : stats.brightness >= 240 ? 'too-bright' : !qualityPassed ? 'low-quality' : ''
  }
}
